import { US_STATES, WEEKDAYS, WORK_COUNTIES } from '../../../shared/constants/application';
import {
  isStrictlyOlderThan18,
  isValidEmail,
  isValidUsPhone,
  isValidZipCode,
} from '../../../shared/validation/application';

const REQUIRED_YES_FIELDS = [
  'authorizedToWork',
  'legallyPermitted',
  'backgroundCheckConsent',
  'independentContractorAcknowledgment',
] as const;
const YES_NO_FIELDS = [
  'hasDriversLicense',
  'hasReliableTransportation',
  'willingToTransport',
  'insured',
  ...REQUIRED_YES_FIELDS,
] as const;
const OTHER_FIELDS = [
  ['languages', 'otherLanguage'],
  ['servicesOffered', 'otherExpertise'],
  ['certifications', 'otherCertification'],
] as const;

export function dedupeValues(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  values.forEach((value) => {
    const trimmedValue = value.trim();
    const key = trimmedValue.toLowerCase();
    if (!trimmedValue || seen.has(key)) return;
    seen.add(key);
    result.push(trimmedValue);
  });

  return result;
}

export function parseCommaSeparatedValues(value: string): string[] {
  return dedupeValues(value.split(','));
}

export function formatUsPhone(value: string): string {
  const inputDigits = value.replace(/\D/g, '');
  const digits = (inputDigits.length === 11 && inputDigits.startsWith('1') ? inputDigits.slice(1) : inputDigits).slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function formatZipCode(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 9);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
}

function isValidReferenceContact(value: string): boolean {
  return value.length <= 254 && (isValidEmail(value) || isValidUsPhone(value));
}

export function isOlderThan18(dateOfBirth: string, today = new Date()): boolean {
  return isStrictlyOlderThan18(dateOfBirth, today);
}

export function validateApplicationForm(data: FormData, caregiver: boolean): string | null {
  const text = (name: string) => String(data.get(name) ?? '').trim();
  const hasLength = (name: string, min: number, max: number) => text(name).length >= min && text(name).length <= max;

  if (
    !text('firstName')
    || !text('lastName')
    || text('firstName').length > 80
    || text('lastName').length > 80
  ) return 'Enter a valid first and last name.';
  if (!isValidEmail(text('email'))) return 'Enter a valid email address.';
  if (text('phone').length > 20 || !isValidUsPhone(text('phone'))) return 'Enter a 10-digit U.S. phone number.';
  if (
    text('line1').length < 3
    || text('line1').length > 150
    || text('line2').length > 150
  ) return 'Enter a complete street address.';
  if (text('city').length < 2 || text('city').length > 100) return 'Enter a valid city.';
  if (!(US_STATES as readonly string[]).includes(text('state'))) return 'Select a valid state.';
  if (!isValidZipCode(text('postalCode'))) return 'Enter a valid 5-digit or ZIP+4 code.';

  if (!caregiver) {
    const services = parseCommaSeparatedValues(text('requestedServices'));
    if (
      !hasLength('careRecipientName', 1, 160)
      || !hasLength('relationshipToRecipient', 1, 100)
    ) return 'Complete all required care-needs fields.';
    if (
      text('requestedServices').length > 500
      || services.length === 0
      || services.length > 20
      || services.some((service) => service.length > 100)
    ) return 'Enter up to 20 requested services, separated by commas.';
    if (text('careNotes').length > 2000) return 'Additional care notes must be 2,000 characters or fewer.';
    return null;
  }

  if (text('middleName').length > 80 || text('preferredName').length > 80) return 'Names must be 80 characters or fewer.';
  if (!isOlderThan18(text('dateOfBirth'))) return 'Caregivers must be older than 18 and enter a valid birth date.';

  const yearsExperienceText = text('yearsExperience');
  const yearsExperience = Number(yearsExperienceText);
  if (
    !yearsExperienceText
    || !Number.isInteger(yearsExperience)
    || yearsExperience < 0
    || yearsExperience > 80
  ) return 'Years of experience must be a whole number from 0 to 80.';

  const maximumTravelMilesText = text('maximumTravelMiles');
  const maximumTravelMiles = Number(maximumTravelMilesText);
  if (
    !maximumTravelMilesText
    || !Number.isInteger(maximumTravelMiles)
    || maximumTravelMiles < 0
    || maximumTravelMiles > 500
  ) return 'Maximum travel distance must be a whole number from 0 to 500 miles.';

  if (data.getAll('languages').length === 0) return 'Select at least one language.';
  if (data.getAll('servicesOffered').length === 0) return 'Select at least one area of expertise.';
  if (data.getAll('certifications').length === 0) return 'Select at least one certification.';
  for (const [group, details] of OTHER_FIELDS) {
    if (data.getAll(group).includes('Other') && !hasLength(details, 1, 500)) {
      return 'Describe each selected “Other” option in 500 characters or fewer.';
    }
  }

  if (!(WORK_COUNTIES as readonly string[]).includes(text('serviceAreas'))) return 'Select a valid county of preference.';
  const priorCareSettings = parseCommaSeparatedValues(text('priorCareSettings'));
  if (
    text('priorCareSettings').length > 500
    || priorCareSettings.length === 0
    || priorCareSettings.length > 20
  ) return 'Enter up to 20 prior care settings, separated by commas.';
  if (!hasLength('caregivingApproach', 1, 2000)) return 'Enter a professional bio of 2,000 characters or fewer.';
  if (text('serviceLimitations').length > 1000) return 'Service limitations must be 1,000 characters or fewer.';
  if (WEEKDAYS.some((day) => !hasLength(`availability-${day}`, 1, 100))) {
    return 'Enter availability for every day, using “Unavailable” where needed.';
  }
  if (YES_NO_FIELDS.some((field) => !['yes', 'no'].includes(text(field)))) {
    return 'Answer every required Yes/No question.';
  }
  if (REQUIRED_YES_FIELDS.some((field) => text(field) !== 'yes')) {
    return 'Answer Yes to work authorization, legal permission, background checks, and the independent caregiver acknowledgment.';
  }

  for (let number = 1; number <= 3; number += 1) {
    if (
      !hasLength(`reference${number}Name`, 1, 100)
      || !hasLength(`reference${number}Relationship`, 1, 100)
    ) return `Complete all fields for reference ${number}.`;
    if (!isValidReferenceContact(text(`reference${number}Contact`))) {
      return `Enter a valid phone number or email for reference ${number}.`;
    }
  }
  if (!hasLength('printedName', 1, 160)) return 'Enter a printed name of 160 characters or fewer.';
  return null;
}
